<!-- src/components/forms/completed/ObservationCompleted.vue -->
<template>
  <div class="space-y-16 animate-fadeIn text-left">
    
    <!-- 1. Report Metadata (Midnight Quality Assurance Header) -->
    <div class="flex flex-wrap items-center justify-between gap-6 p-8 bg-slate-900 rounded-[40px] text-white shadow-2xl relative overflow-hidden">
      <div class="absolute top-0 right-0 p-10 opacity-5">
        <ShieldCheckIcon class="h-32 w-32" />
      </div>
      <div class="relative z-10 space-y-2">
        <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#20FCF1]">Quality Assurance Record</p>
        <h2 class="text-2xl font-black uppercase tracking-tight">{{ formatText(submissionData.observationType || 'Observation Record') }}</h2>
        <div class="flex items-center gap-4 text-slate-400 text-xs font-bold">
          <span>LOCATION: {{ submissionData.observationLocation || 'N/A' }}</span>
          <span class="w-1.5 h-1.5 rounded-full bg-slate-700"></span>
          <span>REF: {{ submissionData.humanReadableId || submissionData.referenceNumber }}</span>
        </div>
      </div>
      <div class="relative z-10 flex flex-col items-end gap-2">
        <span class="bg-[#20FCF1] text-[#0A1630] px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">Observation Complete</span>
        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ formatDate(submissionData.submittedAt) }}</p>
      </div>
    </div>

    <!-- SECTION 1. Observer Details -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <IdentificationIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Observer Details</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="StatCard">
          <span class="label">Full Name</span>
          <span class="value">{{ submissionData.observerName || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Email Address</span>
          <span class="value lowercase">{{ submissionData.observerEmail || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Phone Number</span>
          <span class="value">{{ submissionData.observerPhone || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- SECTION 2. Incident Parameters -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ClipboardDocumentListIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Incident Parameters</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 print:bg-white">
        <div class="space-y-4">
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Date & Time</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ formatDate(submissionData.observationDateTime) }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Observation Type</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ formatText(submissionData.observationType) }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Category</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ formatText(submissionData.category) }}</span>
          </div>
        </div>
        <div class="space-y-4">
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Location</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ submissionData.observationLocation }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Reporter Status</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ submissionData.reportedBy === 'Other' ? `Other (${submissionData.reportedByOther})` : formatText(submissionData.reportedBy) }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Client Rep on Site</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ submissionData.clientRepresentative || 'None' }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 3. Observation Narrative -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ChatBubbleBottomCenterTextIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Observation Narrative</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm">
          <div class="flex items-center gap-2 mb-4">
            <ChatBubbleBottomCenterTextIcon class="h-4 w-4 text-[#20FCF1]" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Description of Identification</h4>
          </div>
          <p class="text-sm text-slate-600 leading-relaxed italic whitespace-pre-wrap">"{{ submissionData.description }}"</p>
        </div>

        <div class="bg-[#EBFDF8] border border-[#20FCF1]/20 rounded-[32px] p-8 shadow-sm print:bg-white">
          <div class="flex items-center gap-2 mb-4">
            <WrenchIcon class="h-4 w-4 text-[#0A1630]" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Remediation Action Taken</h4>
          </div>
          <p class="text-sm text-[#0A1630] font-medium leading-relaxed whitespace-pre-wrap">{{ submissionData.actionTaken || 'No remediation recorded.' }}</p>
        </div>
      </div>
    </section>

    <!-- SECTION 4. Risk & Status Profile -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldExclamationIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Risk & Status Profile</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="StatCard border-l-8 border-l-[#20FCF1]">
          <span class="label text-[#20FCF1]">Potential Likelihood</span>
          <span class="value uppercase">{{ formatText(submissionData.likelihood) }}</span>
        </div>
        <div class="StatCard border-l-8 border-l-[#0A1630]">
          <span class="label text-[#0A1630]">Potential Severity</span>
          <span class="value uppercase">{{ formatText(submissionData.severity) }}</span>
        </div>
        <div class="bg-slate-900 rounded-[24px] p-6 text-white shadow-xl flex flex-col justify-center print:bg-white print:text-slate-900 print:border print:border-slate-200 print:shadow-none">
          <div class="flex items-center justify-between mb-4">
            <span class="text-[9px] font-black uppercase tracking-widest text-slate-400">Compliance Checks</span>
          </div>
          <div class="space-y-2">
            <div class="flex justify-between text-[10px] font-bold">
              <span>TRACKSIDE:</span>
              <span :class="submissionData.isTrackside ? 'text-[#20FCF1] print:text-emerald-600' : 'text-slate-500'">{{ submissionData.isTrackside ? 'YES' : 'NO' }}</span>
            </div>
            <div class="flex justify-between text-[10px] font-bold">
              <span>RESP. INFORMED:</span>
              <span :class="submissionData.responsiblePersonInformed ? 'text-[#20FCF1] print:text-emerald-600' : 'text-slate-500'">{{ submissionData.responsiblePersonInformed ? 'YES' : 'NO' }}</span>
            </div>
            <div class="flex justify-between text-[10px] font-bold">
              <span>CLOSED OUT:</span>
              <span :class="submissionData.closedOut ? 'text-[#20FCF1] print:text-emerald-600' : 'text-slate-500'">{{ submissionData.closedOut ? 'YES' : 'NO' }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 5. Photographic & Supporting Evidence -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CameraIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">5. Photographic & Supporting Evidence</h3>
      </div>
      
      <!-- Placeholder shown when no photos or documents were uploaded -->
      <div v-if="(!submissionData.imageUrls || submissionData.imageUrls.length === 0) && (!submissionData.documentUrls || submissionData.documentUrls.length === 0)" class="p-8 text-center bg-slate-50 border-2 border-dashed border-slate-200 rounded-[28px]">
         <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">No photographic or supporting evidence uploaded</p>
      </div>

      <div v-else class="space-y-6">
        <!-- Sub-Section: Photos -->
        <div v-if="submissionData.imageUrls && submissionData.imageUrls.length > 0" class="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner print:bg-white">
          <div v-for="(url, idx) in submissionData.imageUrls" :key="idx" class="group relative aspect-square overflow-hidden rounded-2xl border-4 border-white shadow-md">
            <img :src="url" class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <a :href="url" target="_blank" class="absolute inset-0 bg-[#0A1630]/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center no-print">
              <MagnifyingGlassIcon class="h-8 w-8 text-white" />
            </a>
          </div>
        </div>

        <!-- Sub-Section: Supporting Documentation -->
        <div v-if="submissionData.documentUrls && submissionData.documentUrls.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner print:bg-white">
          <div v-for="(url, idx) in submissionData.documentUrls" :key="idx" class="flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div class="flex items-center gap-3 overflow-hidden">
                  <DocumentIcon class="h-6 w-6 text-slate-400 shrink-0" />
                  <span class="text-[10px] font-black uppercase text-slate-600 truncate">{{ getFileNameFromUrl(url) }}</span>
              </div>
              <a :href="url" target="_blank" class="px-4 py-2 bg-[#0A1630] text-[#20FCF1] rounded-lg text-[9px] font-black uppercase tracking-widest no-print">View Doc</a>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 6. Official Attestation & Sign-Off (Moved Above Management Review) -->
    <section class="border-t pt-12">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <CheckCircleIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">6. Official Attestation & Sign-Off</h3>
      </div>

      <div class="flex flex-col md:flex-row justify-between items-end gap-12">
        <div class="space-y-6 max-w-lg text-left">
          <div class="p-8 bg-slate-900 rounded-[32px] border border-slate-800 text-slate-300 shadow-lg">
            <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-widest mb-2">Assessor Statement Confirmation</p>
            <p class="text-xs font-medium italic leading-relaxed">
              "I confirm that I have identified the hazards and remediation steps as recorded in this electronic submission."
            </p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl" class="bg-white p-8 rounded-[40px] border border-slate-200 shadow-2xl flex flex-col items-center min-w-[280px]">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-6">Digital Validation Seal</p>
          <img :src="submissionData.signatureDataUrl" alt="Observer Seal" class="max-h-24 object-contain mb-6 grayscale" />
          <div class="w-full h-px bg-slate-100 mb-6"></div>
          <div class="flex items-center gap-2">
            <CheckBadgeIcon class="h-5 w-5 text-[#20FCF1]" />
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-[0.1em]">Verification Passed</span>
          </div>
          <p class="text-[10px] font-bold text-slate-400 mt-3">{{ formatDate(submissionData.submittedAt, 'dateOnly') }}</p>
        </div>
      </div>
    </section>

    <!-- SECTION 7. Management Review (Stage 2 - Interactive) -->
    <section id="management-appraisal" class="pt-16 border-t-8 border-slate-100 animate-fadeIn text-left">
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">7. Management Review</h2>
      </div>

      <div class="bg-[#0A1630] p-8 md:p-12 rounded-[50px] text-white shadow-2xl relative overflow-hidden border-b-[12px] border-[#20FCF1]">
        <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
          <ShieldCheckIcon class="h-64 w-64 text-white" />
        </div>
        
        <div class="relative z-10">
          <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-6 text-left">
            <div>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.4em] mb-3">Stage 2 - Internal Compliance</p>
              <h2 class="text-3xl font-black uppercase tracking-tight leading-none text-left">Senior Management Review</h2>
            </div>
            <div v-if="appraisalData.status" :class="statusClass(appraisalData.status)" class="px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl border border-white/10 backdrop-blur-md">
              {{ appraisalData.status }}
            </div>
          </div>

          <div class="space-y-6">
            <!-- Appraisal Notes -->
            <div>
               <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-3">Management Corrective & Preventive Actions (Mandatory)<span class="text-red-500">*</span></label>
               <textarea 
                 v-model="appraisalData.notes" 
                 :readonly="!hasAppraisalAccess" 
                 rows="5" 
                 class="w-full bg-white/5 border border-white/10 rounded-[24px] p-6 text-sm font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/20" 
                 placeholder="Enter managerial findings, request for information, or final close-out directives..."
               ></textarea>
            </div>

            <!-- Optional Lessons Learned & CAPA Fields -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-3">Lessons Learned (Optional)</label>
                 <textarea 
                   v-model="appraisalData.lessonsLearned" 
                   :readonly="!hasAppraisalAccess" 
                   rows="4" 
                   class="w-full bg-white/5 border border-white/10 rounded-[24px] p-6 text-sm font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/20" 
                   placeholder="Improvements and safety insights identified..."
                 ></textarea>
              </div>
              <div>
                 <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-3">Any Additional Comments (Optional)</label>
                 <textarea 
                   v-model="appraisalData.recommendations" 
                   :readonly="!hasAppraisalAccess" 
                   rows="4" 
                   class="w-full bg-white/5 border border-white/10 rounded-[24px] p-6 text-sm font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/20" 
                   placeholder="Specific, measurable actions..."
                 ></textarea>
              </div>
            </div>

            <!-- AUDIT SEAL -->
            <div v-if="appraisalData.appraisedBy" class="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 animate-fadeIn text-left">
               <div class="flex items-center gap-6">
                  <div class="h-16 w-16 bg-white p-4 rounded-2xl flex items-center justify-center shadow-lg"><CheckCircleIcon class="h-8 w-8 text-[#20FCF1]" /></div>
                  <div>
                     <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-1">Authoritative Close-Out Seal</p>
                     <p class="text-xl font-black text-white uppercase tracking-tight leading-none text-left">{{ appraisalData.appraisedBy }}</p>
                     <p class="text-[9px] font-bold text-slate-500 uppercase mt-2 tracking-widest">Handshake Verified: {{ formatDate(appraisalData.appraisedAt) }}</p>
                  </div>
               </div>
               
               <div v-if="hasAppraisalAccess" class="flex flex-wrap justify-center gap-2">
                 <button type="button" v-for="status in ['In Progress', 'Requires Action', 'Closed/Resolved']" :key="status" @click="appraisalData.status = status" :class="appraisalData.status === status ? 'bg-[#20FCF1] text-[#0A1630] scale-105' : 'bg-white/5 text-white'" class="px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">{{ status }}</button>
               </div>
            </div>

            <div v-if="hasAppraisalAccess" class="mt-8 flex justify-end">
               <button 
                 type="button"
                 @click="saveAppraisal" 
                 :disabled="isSavingAppraisal" 
                 class="bg-[#20FCF1] text-[#0A1630] px-12 py-5 rounded-[24px] font-black uppercase tracking-[0.2em] text-xs shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
               >
                 <span v-if="isSavingAppraisal" class="flex items-center gap-2">
                   <div class="animate-spin h-4 w-4 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                   Syncing...
                 </span>
                 <span v-else>Apply Management Seal</span>
               </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
/* eslint-disable vue/no-mutating-props */
import { ref, reactive, onMounted, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';

import { 
  IdentificationIcon, ClipboardDocumentListIcon, ChatBubbleBottomCenterTextIcon, 
  WrenchIcon, ShieldExclamationIcon, CameraIcon, MagnifyingGlassIcon, 
  CheckCircleIcon, DocumentIcon, ShieldCheckIcon, CheckBadgeIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  formatBoolean: { type: Function, required: true }
});

const userStore = useUserStore();
const isSavingAppraisal = ref(false);

const appraisalData = reactive({
  notes: '',
  status: 'Pending Review',
  lessonsLearned: '',
  recommendations: '',
  appraisedBy: '',
  appraisedAt: null
});

// Always active to support seamless viewing/sealing for all H&S Command Centre users [1]
const hasAppraisalAccess = computed(() => {
  return true;
});

const getFileNameFromUrl = (url) => {
    if (!url) return 'Supporting_File';
    const decoded = decodeURIComponent(url);
    const parts = decoded.split('/');
    const lastPart = parts[parts.length - 1];
    return lastPart.split('?')[0].split('_').pop(); 
};

const statusClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s.includes('CLOSED')) return 'bg-emerald-100 text-emerald-800';
  if (s.includes('ACTION')) return 'bg-rose-500 text-white';
  return 'bg-amber-500 text-[#0A1630]';
};

const saveAppraisal = async () => {
    if (!hasAppraisalAccess.value || isSavingAppraisal.value) return;
    isSavingAppraisal.value = true;
    try {
        // Resolve target document ID using direct keys or by querying humanReadableId
        let targetId = props.submissionData.id || 
                       props.submissionData.docId || 
                       props.submissionData._id || 
                       props.submissionData.uid ||
                       props.submissionData.idField;

        // Dynamic database resolution if the list metadata omitted the document ID
        if (!targetId && props.submissionData.humanReadableId) {
            console.log("Sentinel Safe - Resolving metadata document ID using humanReadableId:", props.submissionData.humanReadableId);
            const q = query(
                collection(db, 'healthsafetyFormSubmissions'), 
                where('humanReadableId', '==', props.submissionData.humanReadableId)
            );
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                targetId = querySnapshot.docs[0].id;
                console.log("Sentinel Safe - Document ID successfully resolved:", targetId);
            }
        }

        if (!targetId) {
            const availableKeys = Object.keys(props.submissionData || {}).join(', ');
            throw new Error(`Firestore document reference is missing. Available submissionData keys: [${availableKeys}]`);
        }

        const docRef = doc(db, 'healthsafetyFormSubmissions', targetId);
        const adminName = `${userStore.userProfile?.firstName || 'System'} ${userStore.userProfile?.lastName || 'Admin'}`;
        const timestamp = new Date().toISOString();
        
        const updateObj = {
            appraisalStage2: {
                notes: appraisalData.notes,
                status: appraisalData.status,
                lessonsLearned: appraisalData.lessonsLearned,
                recommendations: appraisalData.recommendations,
                appraisedBy: adminName,
                appraisedAt: timestamp
            },
            status: appraisalData.status 
        };

        await updateDoc(docRef, updateObj);
        
        // Mutate local props dynamically so that data persists instantly in the current user view
        if (!props.submissionData.appraisalStage2) {
            props.submissionData.appraisalStage2 = {};
        }
        Object.assign(props.submissionData.appraisalStage2, updateObj.appraisalStage2);
        props.submissionData.status = appraisalData.status;

        appraisalData.appraisedBy = adminName;
        appraisalData.appraisedAt = timestamp;
        
        alert("Management Appraisal Registry Synchronised.");
    } catch (e) {
        console.error("Firestore Update Error:", e);
        alert("Registry sync failure: " + e.message);
    } finally {
        isSavingAppraisal.value = false;
    }
};

onMounted(() => {
    if (props.submissionData?.appraisalStage2) {
        Object.assign(appraisalData, props.submissionData.appraisalStage2);
    }
});
</script>

<style scoped>
.StatCard { @apply flex flex-col p-6 bg-white border border-slate-200 rounded-[28px] shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

@media print {
    .space-y-8 { margin-top: 0 !important; }
    section { break-inside: avoid; margin-bottom: 2rem !important; }
}
</style>